import {fmtDate} from '../format'

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Courier weight-scan "photo" — ported from the offline mockup's
 * `PZ.scanImage` (a dark SVG panel listing the hub's scan readings in amber
 * monospace, sitting behind a stylised package graphic).
 *
 * Pure function of (row, idx): no `Math.random`, no `Date.now()`/`new Date()`
 * with no argument. The only "randomness" — the panel's rotation and the
 * synthetic time-of-day — comes from `idx`, so the same dispute always
 * renders the same photo and a second thumbnail (idx 1, 2, ...) differs only
 * in that fixed, derived way, mirroring the mockup's fan-of-photos look.
 *
 * Hex colours are unavoidable in generated SVG markup — this file is the one
 * permitted exception to the "no raw hex" rule (see task-7 brief, GLOBAL
 * CONSTRAINTS #4). Keep every hex literal confined to this module.
 */
export function scanImage(row, idx) {
  const d = (row && row.dims) || {l: 0, b: 0, h: 0}
  const i = Number(idx) || 0
  const hub = `${(row && row.courier) || 'Courier'} Sort Hub`
  const weight = Number(row && row.appliedWeight) || 0
  const time = `${fmtDate(row && row.updatedAt)} 0${3 + i % 6}:${10 + i % 40}:22 AM`

  const rows = [
    ['Location:', hub],
    ['AWB No:', (row && row.awb) || ''],
    ['Length:', `${d.l} cm`],
    ['Width:', `${d.b} cm`],
    ['Height:', `${d.h} cm`],
    ['R.Vol.:', `${Math.round(d.l * d.b * d.h)} cm (cubic)`],
    ['Weight:', `${weight.toFixed(2)} kg`],
    ['Time:', time],
  ]

  const text = rows.map((r, ri) =>
    `<text x="14" y="${20 + ri * 13}" font-family="monospace" font-size="9" fill="#F2C94C">${esc(r[0])}</text>` +
    `<text x="86" y="${20 + ri * 13}" font-family="monospace" font-size="9" fill="#F2C94C">${esc(r[1])}</text>`,
  ).join('')

  const rot = (i % 5) * 7 - 14

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="420" height="300" viewBox="0 0 420 300">
<rect width="420" height="300" fill="#0B0C0E"/>
<rect x="0" y="0" width="420" height="300" fill="url(#v)"/>
<defs><radialGradient id="v" cx="55%" cy="55%" r="70%">
<stop offset="0%" stop-color="#2A2D33"/><stop offset="100%" stop-color="#0B0C0E"/></radialGradient>
<linearGradient id="p" x1="0" y1="0" x2="1" y2="1">
<stop offset="0%" stop-color="#E8E9EB"/><stop offset="55%" stop-color="#BFC3C9"/><stop offset="100%" stop-color="#8E9399"/></linearGradient></defs>
<g transform="translate(250 170) rotate(${rot})">
<rect x="-72" y="-52" width="144" height="104" rx="9" fill="url(#p)"/>
<path d="M-72 -14 H72 M-6 -52 V52" stroke="#9BA1A8" stroke-width="1.4" opacity=".7"/>
<rect x="-34" y="-6" width="62" height="40" rx="3" fill="#FBFBFC" stroke="#A9AEB5"/>
<g fill="#3C4148">${[0, 1, 2, 3, 4, 5].map((n) => `<rect x="${-30 + n * 10}" y="0" width="${n % 2 ? 2 : 4}" height="22"/>`).join('')}</g>
<rect x="-30" y="25" width="52" height="4" rx="1" fill="#6F757C"/>
</g>
<g opacity=".9">${text}</g>
<rect x="330" y="264" width="76" height="18" rx="3" fill="#000" opacity=".55"/>
<text x="368" y="277" text-anchor="middle" font-family="monospace" font-size="9" fill="#8FE3A1">SCAN OK</text>
</svg>`

  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}


/** The five shots the capture form asks for, in its own order. */
const PROOF_SHOTS = [
  ['Package length', 'l'],
  ['Package width', 'b'],
  ['Package height', 'h'],
  ['Shipment on scale', 'kg'],
  ['Package with label', ''],
]

/**
 * The merchant's own proof photo, as the panel can render it: a light,
 * phone-shot-looking card, deliberately unlike the courier's dark scan panel
 * above it so the two are never mistaken for each other at thumbnail size.
 *
 * Same contract as scanImage: pure in (row, idx), no Math.random and no
 * argument-less Date, so a row always renders the same set. `idx` picks which
 * of the five shots this is, which is what makes a strip of thumbnails read as
 * five different facts rather than five copies of one.
 *
 * NOT a real photograph, and it does not pretend to be: the dataset carries a
 * COUNT of merchant images, never files. This draws what that count stands
 * for, so the drawer can show the shape of the evidence instead of a chip that
 * merely asserts it exists.
 */
export function merchantProofImage(row, idx) {
  const d = (row && row.declaredDims) || (row && row.dims) || {l: 0, b: 0, h: 0}
  const i = Math.abs(Number(idx) || 0) % PROOF_SHOTS.length
  const [title, axis] = PROOF_SHOTS[i]
  const kg = Number((row && row.claimedDeadKg) || (row && row.originalWeight) || 0)
  const reading = axis === 'kg' ? kg.toFixed(2) + ' kg' : axis ? (d[axis] || 0) + ' cm' : (row && row.awb) || ''
  const tilt = (i % 2 ? 1 : -1) * (1.5 + i * 0.6)

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 300">
<rect width="420" height="300" fill="#EEF0F3"/>
<g transform="translate(210 158) rotate(${tilt})">
<rect x="-104" y="-70" width="208" height="140" rx="10" fill="#F7F8FA" stroke="#C6CBD2"/>
<path d="M-104 -24 H104 M-8 -70 V70" stroke="#D3D8DE" stroke-width="1.3"/>
<rect x="-58" y="-8" width="86" height="52" rx="4" fill="#FFFFFF" stroke="#BFC5CC"/>
<g fill="#454B53">${[0, 1, 2, 3, 4, 5, 6].map((n) => `<rect x="${-52 + n * 12}" y="0" width="${n % 2 ? 2.5 : 5}" height="30"/>`).join('')}</g>
</g>
${axis && axis !== 'kg' ? `<g stroke="#DB5F5B" stroke-width="2.5" fill="none">
<path d="M58 252 H362"/><path d="M58 244 V260 M362 244 V260"/></g>` : ''}
${axis === 'kg' ? `<g><rect x="140" y="238" width="140" height="30" rx="4" fill="#11151A"/>
<text x="210" y="259" text-anchor="middle" font-family="monospace" font-size="17" fill="#8FE3A1">${esc(reading)}</text></g>` : ''}
<rect x="14" y="14" width="${Math.min(320, 84 + title.length * 7)}" height="24" rx="4" fill="#FFFFFF" opacity=".92"/>
<text x="26" y="31" font-family="sans-serif" font-size="13" fill="#212B36">${esc(title)}</text>
${axis && axis !== 'kg' ? `<text x="210" y="288" text-anchor="middle" font-family="monospace" font-size="13" fill="#DB5F5B">${esc(reading)}</text>` : ''}
${!axis ? `<text x="210" y="288" text-anchor="middle" font-family="monospace" font-size="12" fill="#637381">${esc(reading)}</text>` : ''}
</svg>`

  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

/**
 * A still from the merchant's packing video.
 *
 * Same contract and the same honesty as merchantProofImage: the dataset stores
 * `hasVideo` as a boolean, never a file, so there is no footage to play. This
 * draws the frame that boolean stands for and says so on the frame itself —
 * the alternative was a chip the merchant could not open at all, which tells
 * them even less about what was sent.
 */
export function packingVideoFrame(row) {
  const kg = Number((row && row.claimedDeadKg) || (row && row.originalWeight) || 0)
  const awb = (row && row.awb) || ''

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 300">
<rect width="420" height="300" fill="#14181D"/>
<g transform="translate(210 150)">
<rect x="-96" y="-64" width="192" height="128" rx="10" fill="#D8DCE1"/>
<path d="M-96 -20 H96 M-4 -64 V64" stroke="#AEB4BB" stroke-width="1.4"/>
<rect x="-52" y="-4" width="80" height="48" rx="4" fill="#FBFBFC" stroke="#A9AEB5"/>
<g fill="#3C4148">${[0, 1, 2, 3, 4, 5, 6].map((n) => `<rect x="${-46 + n * 11}" y="4" width="${n % 2 ? 2.5 : 5}" height="28"/>`).join('')}</g>
</g>
<circle cx="210" cy="150" r="30" fill="#000" opacity=".45"/>
<path d="M201 136 L227 150 L201 164 Z" fill="#FFFFFF" opacity=".95"/>
<rect x="0" y="0" width="420" height="30" fill="#000" opacity=".55"/>
<circle cx="20" cy="15" r="5" fill="#DB5F5B"/>
<text x="34" y="20" font-family="monospace" font-size="12" fill="#FFFFFF">REC · packing video</text>
<rect x="0" y="262" width="420" height="38" fill="#000" opacity=".55"/>
<text x="14" y="279" font-family="monospace" font-size="11" fill="#C9CED4">AWB ${esc(awb)}</text>
<text x="14" y="293" font-family="sans-serif" font-size="10" fill="#8B929A">Still from the packing video on file — ${esc(kg.toFixed(2))} kg declared</text>
</svg>`

  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

/**
 * Which pictures a row actually has — the real ones on a live row, the drawings on a seeded one.
 *
 * A LIVE row (data/api.js#mapRowFromApi) always carries the server's signed S3 links:
 * `courierImageLinks` (the courier's proof, uploaded by Prozo ops or re-hosted from the MIS),
 * `merchantImageLinks` and `videoLink` (what the merchant uploaded). Only those are ever shown for
 * it — a count with no link shows nothing rather than a drawing, because a merchant disputes a
 * charge by looking at this photo, and a generated plate reading "01 Jan 1970" is not evidence.
 * The seeded demo rows carry counts only, and keep the generated images above.
 */
const isLive = (row) => !!row && Array.isArray(row.courierImageLinks)

/** How many courier images the row has — cheap enough for every grid row, no drawing involved. */
export function courierScanCount(row) {
  if (!row) return 0
  return isLive(row) ? row.courierImageLinks.filter(Boolean).length : (row.courierImages || 0)
}

/** The courier images as <img> sources, in upload order. */
export function courierScanSrcs(row) {
  if (!row) return []
  if (isLive(row)) return row.courierImageLinks.filter(Boolean)
  return Array.from({length: row.courierImages || 0}, (_, i) => scanImage(row, i))
}

/**
 * The merchant's evidence, video first, as {key, isVideo, name, src, href}. `href` is set only on a
 * live video: `src` is then the video file itself, which the strip plays as its own thumbnail and
 * opens in a new tab, since the image viewer shows images only.
 */
export function merchantEvidenceMedia(row) {
  if (!row) return []
  if (isLive(row)) {
    return [
      ...(row.videoLink ? [{key: 'video', isVideo: true, name: 'the packing video', src: row.videoLink, href: row.videoLink}] : []),
      ...(row.merchantImageLinks || []).filter(Boolean).map((src, i) => ({
        key: `img-${i}`, isVideo: false, name: `your evidence photo ${i + 1}`, src,
      })),
    ]
  }
  return [
    ...(row.hasVideo ? [{key: 'video', isVideo: true, name: 'the packing video', src: packingVideoFrame(row)}] : []),
    ...Array.from({length: row.merchantImages || 0}, (_, i) => ({
      key: `img-${i}`, isVideo: false, name: `your evidence photo ${i + 1}`, src: merchantProofImage(row, i),
    })),
  ]
}
